import React, { createContext, useContext, useState, useEffect } from "react";
import { db, storage } from "../firebase";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import {
    doc,
    setDoc,
    getDoc,
    updateDoc,
    arrayUnion,
    onSnapshot,
    FieldPath
} from "firebase/firestore";
import { useAuth } from "./AuthContext";

const FamilyContext = createContext();

export function useFamily() {
    return useContext(FamilyContext);
}

export function FamilyProvider({ children }) {
    const { currentUser } = useAuth();
    const [familyData, setFamilyData] = useState(null);
    const [activeProfileId, setActiveProfileId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!currentUser) {
            setFamilyData(null);
            setLoading(false);
            return;
        }

        const familyDocRef = doc(db, "families", currentUser.uid);

        const unsubscribe = onSnapshot(
            familyDocRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();

                    // ── Migration: backfill missing fields for pre-existing docs ──
                    // Documents created before the wishlist feature won't have this field,
                    // which causes Firestore security rules to reject writes.
                    if (!data.wishlist) {
                        const wishlistSeed = {};
                        (data.members || []).forEach(m => { wishlistSeed[m.id] = {}; });
                        updateDoc(familyDocRef, { wishlist: wishlistSeed }).catch(() => {});
                    }

                    setFamilyData(data);
                    if (!activeProfileId || (data.members && !data.members.find(m => m.id === activeProfileId))) {
                        if (data.members && data.members.length > 0) {
                            setActiveProfileId(data.members[0].id);
                        }
                    }
                } else {
                    const defaultName = currentUser.displayName || "Me";
                    const initialData = {
                        familyName: "My Family",
                        members: [
                            { id: "parent", name: defaultName, role: "admin" }
                        ],
                        progress: {
                            "parent": {}
                        },
                        wishlist: {
                            "parent": {}
                        }
                    };
                    setDoc(familyDocRef, initialData);
                }
                setLoading(false);
            },
            (error) => {
                // Suppress expected permission-denied errors that occur transiently
                // during auth token refresh or sign-out. Real errors are still logged.
                if (error.code !== 'permission-denied') {
                    console.error('Firestore snapshot error:', error);
                }
                setLoading(false);
            }
        );

        return unsubscribe;
    }, [currentUser]);

    async function renameMember(memberId, newName) {
        if (!currentUser || !newName.trim()) return;
        const familyDocRef = doc(db, "families", currentUser.uid);
        const updatedMembers = familyData.members.map(m =>
            m.id === memberId ? { ...m, name: newName.trim() } : m
        );
        await updateDoc(familyDocRef, { members: updatedMembers });
    }

    async function addMember(name) {
        if (!currentUser) return;
        const newMemberId = `member_${Date.now()}`;
        const newMember = { id: newMemberId, name, role: "child" };

        const familyDocRef = doc(db, "families", currentUser.uid);
        await updateDoc(familyDocRef, {
            members: arrayUnion(newMember),
            [`progress.${newMemberId}`]: {},
            [`wishlist.${newMemberId}`]: {}
        });
    }

    // Get the raw entry for an item (object or null)
    function getItemEntry(itemId) {
        if (!familyData || !activeProfileId || !familyData.progress) return null;
        const memberProgress = familyData.progress[activeProfileId];
        if (!memberProgress) return null;
        const entry = memberProgress[itemId];
        return (entry && typeof entry === 'object') ? entry : null;
    }

    async function updateProgress(rideId, isCompleted) {
        if (!currentUser || !activeProfileId) return;
        const familyDocRef = doc(db, "families", currentUser.uid);
        const fp = new FieldPath('progress', activeProfileId, rideId);
        const existingEntry = getItemEntry(rideId);
        const existingNote = existingEntry?.note || null;
        const existingPhoto = existingEntry?.photoUrl || null;

        const now = new Date().toISOString();
        await updateDoc(familyDocRef, fp,
            isCompleted
                ? { completed: true, count: 1, timestamp: now, timestamps: [now], photoUrl: existingPhoto, note: existingNote }
                : (existingNote ? { completed: false, note: existingNote } : null)
        );
    }

    // --- Multi-visit counting ---
    function getVisitCount(itemId) {
        const entry = getItemEntry(itemId);
        if (!entry) return 0;
        if (entry.count != null) return entry.count;
        // Backward compat: old data has completed:true but no count field
        return entry.completed ? 1 : 0;
    }

    async function updateVisitCount(itemId, newCount) {
        if (!currentUser || !activeProfileId) return;
        const familyDocRef = doc(db, "families", currentUser.uid);
        const fp = new FieldPath('progress', activeProfileId, itemId);
        const existingEntry = getItemEntry(itemId) || {};
        const existingNote = existingEntry.note || null;
        const existingPhoto = existingEntry.photoUrl || null;
        let existingTimestamps = existingEntry.timestamps || [];

        // Backward compat: seed timestamps array from legacy single timestamp
        if (existingEntry.completed && existingTimestamps.length === 0 && existingEntry.timestamp) {
            existingTimestamps = [existingEntry.timestamp];
        }

        if (newCount <= 0) {
            // Uncheck completely — same as current behavior
            await updateDoc(familyDocRef, fp,
                existingNote ? { completed: false, note: existingNote } : null
            );
        } else {
            const now = new Date().toISOString();
            let updatedTimestamps = [...existingTimestamps];

            if (newCount > existingTimestamps.length) {
                // Adding visits — append new timestamps
                const toAdd = newCount - existingTimestamps.length;
                for (let i = 0; i < toAdd; i++) updatedTimestamps.push(now);
            } else if (newCount < existingTimestamps.length) {
                // Removing visits — trim from end
                updatedTimestamps = updatedTimestamps.slice(0, newCount);
            }

            // Safety cap: max 100 timestamps per item
            if (updatedTimestamps.length > 100) updatedTimestamps = updatedTimestamps.slice(-100);

            await updateDoc(familyDocRef, fp, {
                completed: true,
                count: newCount,
                timestamp: updatedTimestamps[updatedTimestamps.length - 1],
                timestamps: updatedTimestamps,
                photoUrl: existingPhoto,
                note: existingNote,
            });
        }
    }

    async function addPhoto(itemId, file) {
        if (!currentUser || !activeProfileId || !file) return;
        // Sanitize itemId for use as a storage path segment
        const safeItemId = itemId.replace(/[/~*[\]]/g, '_');
        const filePath = `photos/${currentUser.uid}/${activeProfileId}/${safeItemId}`;
        const fileRef = storageRef(storage, filePath);

        // Upload raw File to Cloud Storage
        await uploadBytes(fileRef, file);
        const downloadUrl = await getDownloadURL(fileRef);

        // Save the URL (not base64) to Firestore
        const familyDocRef = doc(db, "families", currentUser.uid);
        const fp = new FieldPath('progress', activeProfileId, itemId, 'photoUrl');
        await updateDoc(familyDocRef, fp, downloadUrl);
    }

    function getRideStatus(rideId) {
        if (!familyData || !activeProfileId || !familyData.progress) return false;
        const memberProgress = familyData.progress[activeProfileId];
        const entry = memberProgress ? memberProgress[rideId] : null;
        return entry && entry.completed ? true : false;
    }

    // --- Photo URL ---
    function getPhotoUrl(itemId) {
        const entry = getItemEntry(itemId);
        return entry?.photoUrl || null;
    }

    // --- Notes ---
    function getNote(itemId) {
        const entry = getItemEntry(itemId);
        return entry?.note || '';
    }

    async function updateNote(itemId, note) {
        if (!currentUser || !activeProfileId) return;
        const familyDocRef = doc(db, "families", currentUser.uid);
        const trimmedNote = note?.trim() || null;
        const entry = getItemEntry(itemId);

        if (entry) {
            const fp = new FieldPath('progress', activeProfileId, itemId, 'note');
            await updateDoc(familyDocRef, fp, trimmedNote);
        } else if (trimmedNote) {
            const fp = new FieldPath('progress', activeProfileId, itemId);
            await updateDoc(familyDocRef, fp, { completed: false, note: trimmedNote });
        }
    }

    // --- Wishlist ---
    function getWishlistStatus(itemId) {
        if (!familyData || !activeProfileId || !familyData.wishlist) return false;
        const memberWishlist = familyData.wishlist[activeProfileId];
        return memberWishlist ? !!memberWishlist[itemId] : false;
    }

    async function toggleWishlist(itemId) {
        if (!currentUser || !activeProfileId) return;
        const familyDocRef = doc(db, "families", currentUser.uid);
        const fp = new FieldPath('wishlist', activeProfileId, itemId);
        const currentStatus = getWishlistStatus(itemId);
        await updateDoc(familyDocRef, fp, currentStatus ? null : true);
    }

    const value = {
        familyData,
        activeProfileId,
        setActiveProfileId,
        addMember,
        renameMember,
        updateProgress,
        addPhoto,
        getRideStatus,
        getVisitCount,
        updateVisitCount,
        getPhotoUrl,
        getNote,
        updateNote,
        getWishlistStatus,
        toggleWishlist,
        loading
    };

    return (
        <FamilyContext.Provider value={value}>
            {!loading && children}
        </FamilyContext.Provider>
    );
}
